import './StreamingWizard.scss';
import * as Dfns from 'date-fns/fp';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Form, ListGroup, Table } from 'react-bootstrap';
import { Route, useNavigate, useParams } from 'react-router-dom';
import { Routes } from '@datadog/browser-rum-react/react-router-v6';
import cn from 'classnames';

import { Button } from '@Components/Button/Button';
import { BreadcrumbTitle } from '@Components/BreadcrumbTitle/BreadcrumbTitle';
import { DateBox } from '@Components/DateBox';
import { DatePicker } from '@Components/DatePicker';
import { Appearance, Size } from '@Components/designConstants';
import { EntityKind, EntityLogo, EntitySize } from '@Components/EntityLogo';
import { InteractiveDiv } from '@Components/InteractiveEl/InteractiveEl';
import { Page } from '@Components/Page';
import { FullPageSpinner } from '@Components/Spinner';
import { BPMTable, Header } from '@Components/StickyTable/BPMTable';
import { TableLoading } from '@Components/TableEmptyState';
import { BPMTableWidget } from '@Components/BPMTableWidget';
import { useSetError } from '../../redux/modals';
import { usePageTitleNav } from '../../utils/hooks/useNav';
import {
  FlightData,
  submitWizardAction,
  SubFlightData,
  UnmatchedGroup,
  useStreamingWizardData,
  WizardData,
  WizardDateRange
} from './use-streaming-wizard';
import {
  buildFlightAdvice,
  byDateDescending,
  MAX_COUNT,
  toPrettyCPM,
  toPrettyDate,
  toPrettyNumber
} from './streaming-wizard-utils';
import { formatWholeUsd } from '../../utils/text-utils';

const DATE_FORMAT = 'yyyy-MM-dd';

const ROUTES = [
  {
    key: 'details',
    label: '*',
    uri: '*'
  }
];

const ACCEPT = 'Accept';
const EXTEND = 'Extend';
const ADD = 'Add';
type ActionKey = typeof ACCEPT | typeof EXTEND | typeof ADD;

// A selected flight is either nothing (`[]`), a top-level flight (`[flightId]`), or a sub-flight
// (`["sub", subFlightId, parentFlightId]`).
type SelectedFlight = (string | number)[];

interface WizardContextValue {
  setMessage: Dispatch<SetStateAction<string>>;
  setSaving: Dispatch<SetStateAction<boolean>>;
  resetWizard: () => void;
}

const WizardContext = createContext<WizardContextValue>({
  setMessage: () => {},
  setSaving: () => {},
  resetWizard: () => {}
});

// TODO: alex messages
// - Extending a flight beyond the last date of unmatched impressions
// - Not extending a flight far enough

interface WizardRow {
  derivedNetwork: string;
  network: string;
  description: string;
  platform: string;
  count: number;
  start: string;
  end: string;
}

interface WizardListProps {
  wizardData?: WizardData;
  isLoading: boolean;
}

const WizardList = ({ wizardData, isLoading }: WizardListProps) => {
  const navigate = useNavigate();
  const { unmatchedGroups, networkData } = wizardData ?? {};
  const { setMessage } = useContext(WizardContext);

  useEffect(() => {
    if (unmatchedGroups) {
      if (unmatchedGroups.length) {
        const big = unmatchedGroups.find(group => group.count > MAX_COUNT);
        if (big) {
          setMessage(
            `You have one with ${toPrettyNumber(
              big.count
            )}. That seems like a lot. Better check that one carefully.`
          );
        } else {
          setMessage('None of these look that big. Probably all bonus.');
        }
      } else {
        setMessage('Huzzah!!!');
      }
    } else {
      setMessage('');
    }
  }, [unmatchedGroups, setMessage]);

  const rows: WizardRow[] = useMemo(
    () =>
      (unmatchedGroups ?? []).map(({ count, derivedNetwork, start, end }) => {
        const { description, network, platform } = networkData?.[derivedNetwork] || {};
        return {
          derivedNetwork,
          count,
          start,
          end,
          description: description ?? '',
          network: network ?? '',
          platform: platform ?? ''
        };
      }),
    [unmatchedGroups, networkData]
  );

  const headers: Header[] = useMemo(() => {
    const cell = (row: WizardRow, content: ReactNode, testId?: string) => (
      <InteractiveDiv
        className='wizard-list-cell'
        data-testid={testId}
        onClick={() => navigate(row.derivedNetwork)}
      >
        <span className='wizard-list-cell-text'>{content}</span>
      </InteractiveDiv>
    );
    return [
      {
        label: 'Network',
        name: 'network',
        width: 80,
        contentGetter: (row: WizardRow) => row.network,
        renderer: (row: WizardRow) =>
          cell(row, <EntityLogo kind={EntityKind.NETWORK} id={row.network} size={Size.LG} />)
      },
      {
        label: 'Derived Network',
        name: 'derivedNetwork',
        flex: 1,
        minFlexWidth: 160,
        contentGetter: (row: WizardRow) => row.derivedNetwork,
        renderer: (row: WizardRow) =>
          cell(row, row.derivedNetwork, `network-row-${row.derivedNetwork}`)
      },
      {
        label: 'Description',
        name: 'description',
        flex: 2,
        minFlexWidth: 200,
        contentGetter: (row: WizardRow) => row.description,
        renderer: (row: WizardRow) => cell(row, row.description)
      },
      {
        label: 'Platform',
        name: 'platform',
        flex: 1,
        minFlexWidth: 110,
        contentGetter: (row: WizardRow) => row.platform,
        renderer: (row: WizardRow) => cell(row, row.platform)
      },
      {
        label: 'Count',
        name: 'count',
        width: 80,
        contentGetter: (row: WizardRow) => row.count,
        renderer: (row: WizardRow) => cell(row, toPrettyNumber(row.count))
      },
      {
        label: 'Start',
        name: 'start',
        width: 110,
        contentGetter: (row: WizardRow) => row.start,
        renderer: (row: WizardRow) => cell(row, toPrettyDate(row.start))
      },
      {
        label: 'End',
        name: 'end',
        width: 110,
        contentGetter: (row: WizardRow) => row.end,
        renderer: (row: WizardRow) => cell(row, toPrettyDate(row.end))
      }
    ];
  }, [navigate]);

  const renderTableLoading = useCallback(
    () => (
      <TableLoading isLoading={isLoading} emptyMessage='No unmatched impressions!' rowHeight={65} />
    ),
    [isLoading]
  );

  return (
    <div className='wizard-list' data-testid='wizard-list'>
      <BPMTableWidget className='wizard-list-widget' tableClassName='wizard-list-table'>
        <BPMTable
          data={rows}
          headers={headers}
          rowHeight={65}
          rowHoverClass='wizard-list-row-hover'
          noRowsRenderer={renderTableLoading}
        />
      </BPMTableWidget>
    </div>
  );
};

const Accept = ({
  selectedFlight,
  unmatchedInfo
}: {
  selectedFlight: SelectedFlight;
  unmatchedInfo: UnmatchedGroup;
}) => {
  const { setSaving, resetWizard } = useContext(WizardContext);
  const setError = useSetError();

  const [because, setBecause] = useState('');

  const onSave = useCallback(async () => {
    setSaving(true);
    try {
      await submitWizardAction({
        derivedNetwork: unmatchedInfo.derivedNetwork,
        type: 'ACCEPT',
        dates: unmatchedInfo.dates.map(({ date }) => date),
        because
      });
      resetWizard();
    } catch (e) {
      setError({
        message: `Couldn't submit wizarding. Error: ${e.message}`,
        reportError: e
      });
      setSaving(false);
    }
  }, [setSaving, setError, resetWizard, unmatchedInfo, because]);
  if (selectedFlight.length) {
    return (
      <div>
        You cannot accept impressions as bonus &quot;on a flight&quot;. Please un-select the flight.
      </div>
    );
  }
  return (
    <>
      <Form.Control
        as='textarea'
        className='because'
        data-testid='because-input'
        placeholder='Because...'
        value={because}
        onChange={e => setBecause(e.target.value)}
      />
      <Button size={Size.LG} className='submit-button' data-testid='submit-action' onClick={onSave}>
        Accept {toPrettyNumber(unmatchedInfo.count)} impression
        {unmatchedInfo.count === 1 ? '' : 's'} as bonus
      </Button>
    </>
  );
};

const Extend = ({
  selectedFlight,
  flights
}: {
  selectedFlight: SelectedFlight;
  flights: FlightData[];
}) => {
  const setError = useSetError();
  const { setSaving, resetWizard } = useContext(WizardContext);

  const isSubFlight = selectedFlight[0] === 'sub';
  const [flight, maxExtended] = useMemo(() => {
    if (!selectedFlight.length) {
      return [undefined, Infinity] as const;
    }

    // Flights are coming to us pre-sorted. If I keep track of the last start date, once I find the
    // flight in question, I know I can only extend it up to that date.
    let lastStart: string | undefined;
    let ourFlight: FlightData | SubFlightData | undefined;
    for (const candidateFlight of flights) {
      if (candidateFlight.id === selectedFlight[isSubFlight ? 2 : 0]) {
        if (isSubFlight) {
          for (const sub of candidateFlight.subFlights ?? []) {
            if (sub.id === selectedFlight[1]) {
              ourFlight = sub;
              break;
            } else {
              lastStart = sub.start;
            }
          }
        } else {
          ourFlight = candidateFlight;
        }
        break;
      } else if (!isSubFlight) {
        lastStart = candidateFlight.start;
      }
    }
    let computedMaxExtended = Infinity;
    if (lastStart && ourFlight?.end) {
      computedMaxExtended =
        Dfns.differenceInDays(Dfns.parseISO(ourFlight.end), Dfns.parseISO(lastStart)) - 1;
    }
    return [ourFlight, computedMaxExtended] as const;
  }, [isSubFlight, selectedFlight, flights]);

  const [days, setDays] = useState(1);
  const [daysRaw, setDaysRaw] = useState('1');
  const newExtended = useMemo(() => {
    if (!flight?.end) {
      return null;
    }
    return Dfns.format(DATE_FORMAT)(Dfns.addDays(days)(Dfns.parseISO(flight.end)));
  }, [flight, days]);

  const onSave = useCallback(async () => {
    if (!flight || !newExtended) {
      return;
    }
    setSaving(true);
    try {
      await submitWizardAction({
        derivedNetwork: flight.derived_network,
        type: 'EXTEND',
        newExtended,
        id: flight.id,
        isSubFlight
      });
      resetWizard();
    } catch (e) {
      setError({
        message: `Couldn't submit wizarding. Error: ${e.message}`,
        reportError: e
      });
      setSaving(false);
    }
  }, [setSaving, setError, resetWizard, flight, newExtended, isSubFlight]);
  if (!selectedFlight.length) {
    return <div>Please select a flight from the list above to extend.</div>;
  }
  if (isSubFlight && !flight?.end) {
    return <div>You can&apos;t extend a sub flight past its parent.</div>;
  }
  return (
    <div>
      Extend flight <strong>#{flight?.id}</strong> by{' '}
      <Form.Control
        className='days-input'
        data-testid='days-input'
        size='sm'
        type='number'
        min='0'
        max={maxExtended}
        value={daysRaw}
        onChange={e => {
          const val = e.target.value;
          setDaysRaw(val);
          const parsed = parseInt(val, 10);
          if (!Number.isNaN(parsed)) {
            setDays(parsed);
          }
        }}
        onBlur={() => {
          const cleanVal = Math.min(maxExtended, Math.abs(parseInt(daysRaw, 10) || 0));
          setDays(cleanVal);
          setDaysRaw(String(cleanVal));
        }}
      />{' '}
      day{days === 1 ? '' : 's'}, making the extended end date{' '}
      <strong>{newExtended && toPrettyDate(newExtended)}</strong>.
      <Button
        size={Size.LG}
        className='submit-button'
        data-testid='submit-action'
        disabled={!days}
        onClick={onSave}
      >
        Extend Flight
      </Button>
    </div>
  );
};

const Add = ({
  selectedFlight,
  flights,
  unmatchedInfo
}: {
  selectedFlight: SelectedFlight;
  flights: FlightData[];
  unmatchedInfo: UnmatchedGroup;
}) => {
  const setError = useSetError();
  const { setSaving, resetWizard } = useContext(WizardContext);

  const isSubFlight = selectedFlight[0] === 'sub';
  const flight = useMemo(() => {
    if (!selectedFlight.length || isSubFlight) {
      return undefined;
    }
    return flights.find(f => f.id === selectedFlight[0]);
  }, [selectedFlight, flights, isSubFlight]);

  const [startDate, setStartDate] = useState<string>();
  const [days, setDays] = useState(0);
  const [daysRaw, setDaysRaw] = useState('0');

  const newExtended = useMemo(() => {
    if (!flight) {
      return null;
    }
    return Dfns.format(DATE_FORMAT)(Dfns.addDays(days)(Dfns.parseISO(startDate || flight.start)));
  }, [flight, startDate, days]);

  const onSave = useCallback(async () => {
    if (!flight || !newExtended) {
      return;
    }
    setSaving(true);
    try {
      await submitWizardAction({
        derivedNetwork: unmatchedInfo.derivedNetwork,
        type: 'ADD',
        startDate,
        newExtended,
        id: flight.id
      });
      resetWizard();
    } catch (e) {
      setError({
        message: `Couldn't submit wizarding. Error: ${e.message}`,
        reportError: e
      });
      setSaving(false);
    }
  }, [setSaving, setError, resetWizard, unmatchedInfo, flight, newExtended, startDate]);

  if (!selectedFlight.length) {
    return <div>Please select a flight from the list above to extend.</div>;
  }
  if (isSubFlight) {
    return <div>You cannot add a sub flight to a sub flight.</div>;
  }
  return (
    <div>
      Add zero-length sub-flight to flight <strong>#{flight?.id}</strong> starting on{' '}
      <DatePicker
        isSingle
        isClearable
        placeholder='same day as flight'
        range={{ start: startDate ?? '', end: startDate ?? '' }}
        onChange={({ start }) => setStartDate(start || undefined)}
        isOutsideRange={date =>
          !flight || date < flight.start || date > (flight.extended || flight.end)
        }
      />{' '}
      and extending{' '}
      <Form.Control
        className='days-input'
        data-testid='days-input'
        size='sm'
        type='number'
        min='0'
        value={daysRaw}
        onChange={e => {
          const val = e.target.value;
          setDaysRaw(val);
          const parsed = parseInt(val, 10);
          if (!Number.isNaN(parsed)) {
            setDays(parsed);
          }
        }}
        onBlur={() => {
          const cleanVal = Math.abs(parseInt(daysRaw, 10) || 0);
          setDays(cleanVal);
          setDaysRaw(String(cleanVal));
        }}
      />{' '}
      day{days === 1 ? '' : 's'}, making the extended end date{' '}
      <strong>{newExtended && toPrettyDate(newExtended)}</strong>.
      <Button
        size={Size.LG}
        className='submit-button'
        data-testid='submit-action'
        disabled={!newExtended}
        onClick={onSave}
      >
        Add Property to Flight
      </Button>
    </div>
  );
};

const ActionBox = ({
  action,
  selectedFlight,
  flights,
  unmatchedInfo
}: {
  action?: ActionKey;
  selectedFlight: SelectedFlight;
  flights: FlightData[];
  unmatchedInfo: UnmatchedGroup;
}) => {
  let content: ReactNode;
  switch (action) {
    case ACCEPT:
      content = <Accept selectedFlight={selectedFlight} unmatchedInfo={unmatchedInfo} />;
      break;
    case EXTEND:
      content = <Extend selectedFlight={selectedFlight} flights={flights} />;
      break;
    case ADD:
      content = (
        <Add selectedFlight={selectedFlight} flights={flights} unmatchedInfo={unmatchedInfo} />
      );
      break;
    default:
      return null;
  }
  return (
    <div className='action-box' data-testid='action-box'>
      {content}
    </div>
  );
};

const Details = ({ wizardData }: { wizardData: WizardData }) => {
  const { derivedNetwork = '' } = useParams<{ derivedNetwork: string }>();
  const { setMessage } = useContext(WizardContext);

  const networkData = wizardData.networkData[derivedNetwork];
  const unmatchedInfo = wizardData.unmatchedGroups.find(
    group => group.derivedNetwork === derivedNetwork
  );

  const [selectedFlight, setSelectedFlight] = useState<SelectedFlight>([]);
  const [selectedAction, setSelectedAction] = useState<ActionKey>();

  const hasSubs = Boolean(networkData?.parent);
  const actionKeys: ActionKey[] = hasSubs ? [ACCEPT, EXTEND, ADD] : [ACCEPT, EXTEND];

  const sortedImpRows = useMemo(
    () => (unmatchedInfo ? [...unmatchedInfo.dates].sort(byDateDescending) : null),
    [unmatchedInfo]
  );

  // TODO: make a method for action box components to pass messages

  useEffect(() => {
    setMessage('');
  }, [setMessage]);
  useEffect(() => {
    if (unmatchedInfo && sortedImpRows && networkData) {
      setMessage(
        buildFlightAdvice({ unmatchedInfo, sortedImpRows, networkData, hasSubs, derivedNetwork })
      );
    }
  }, [setMessage, sortedImpRows, networkData, unmatchedInfo, hasSubs, derivedNetwork]);

  if (!(networkData && unmatchedInfo && sortedImpRows)) {
    return (
      <div className='no-wizard-required' data-testid='wizard-details'>
        No Data
      </div>
    );
  }
  return (
    <div className='details' data-testid='wizard-details'>
      <div className='header'>
        <div className='logo'>
          <EntityLogo kind={EntityKind.NETWORK} id={networkData.network} size={EntitySize.XL} />
        </div>
        <div className='header-section'>
          <div className='top'>
            <div className='derivedID'>{derivedNetwork}</div>
            <div className='dash' />
            <div className='network'>
              {networkData.network}
              {networkData.parent && <small>(child of {networkData.parent})</small>}
            </div>
            {networkData.description && (
              <>
                <div className='dash' />
                <div className='description'>{networkData.description}</div>
              </>
            )}
            <div className='dash' />
            <div className='platform'>{networkData.platform}</div>
            {networkData.dsp && (
              <>
                <div className='dash' />
                <div className='dsp'>{networkData.dsp}</div>
              </>
            )}
          </div>
          <div className='bottom'>
            <strong>{toPrettyNumber(unmatchedInfo.count)}</strong>impression
            {unmatchedInfo.count === 1 ? '' : 's'} from{' '}
            <strong>{toPrettyDate(unmatchedInfo.start)}</strong>through{' '}
            <strong>{toPrettyDate(unmatchedInfo.end)}</strong>
          </div>
        </div>
      </div>
      <div className='wizard-body'>
        <div className='flight-list'>
          <ListGroup>
            {networkData.flights.map(
              ({ id, order_id, start, end, extended, spend, cpm, canceled, subFlights }) => (
                <>
                  <ListGroup.Item
                    key={id}
                    data-testid={`flight-row-${id}`}
                    className={cn('flight-item', { selected: selectedFlight[0] === id })}
                    action
                    onClick={() => setSelectedFlight(selected => (selected[0] === id ? [] : [id]))}
                  >
                    <div className='id-box'>
                      <div>ID: {id}</div>
                      <div className='sub-box'>(order {order_id})</div>
                    </div>

                    <div className='spend-info'>
                      <div>{formatWholeUsd(spend)}</div>
                      <div className='sub-box'>(CPM: {toPrettyCPM(cpm)})</div>
                    </div>
                    <div className='dateBox'>
                      <DateBox start={start} end={end} />
                      <div className='sub-box'>
                        {extended && <small>(extended to {toPrettyDate(extended)})</small>}
                      </div>
                    </div>
                    {canceled && <div className='canceled-box'>Canceled</div>}
                  </ListGroup.Item>
                  {subFlights &&
                    subFlights.map(
                      ({
                        id: subID,
                        start: subStart,
                        end: subEnd,
                        extended: subExtended,
                        zero_length
                      }) => (
                        <ListGroup.Item
                          key={subID}
                          action
                          className={cn('flight-item', 'sub-flight', {
                            selected: selectedFlight[1] === subID
                          })}
                          onClick={() =>
                            setSelectedFlight(selected =>
                              selected[1] === subID ? [] : ['sub', subID, id]
                            )
                          }
                        >
                          <div className='id-box'>ID: {subID}</div>
                          {zero_length && <div className='zero-length'>Zero-length</div>}
                          <div className='dateBox'>
                            <DateBox
                              preformatted
                              start={subStart ? toPrettyDate(subStart) : 'same as parent'}
                              end={subEnd ? toPrettyDate(subEnd) : 'same as parent'}
                            />
                            <div className='sub-box'>
                              {subExtended && (
                                <small>(extended to {toPrettyDate(subExtended)})</small>
                              )}
                            </div>
                          </div>
                        </ListGroup.Item>
                      )
                    )}
                </>
              )
            )}
          </ListGroup>
        </div>
        <div className='impression-table'>
          <Table striped bordered size='sm'>
            <thead>
              <tr>
                <th>Date</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {sortedImpRows.map(({ date, count }) => (
                <tr key={date}>
                  <td>{toPrettyDate(date)}</td>
                  <td>{toPrettyNumber(count)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
      <div className='wizard-action'>
        <div className='action-buttons'>
          {actionKeys.map(key => (
            <Button
              size={Size.LG}
              key={key}
              appearance={selectedAction === key ? Appearance.FILLED : Appearance.OUTLINED}
              onClick={() => setSelectedAction(current => (current === key ? undefined : key))}
            >
              {key}
            </Button>
          ))}
        </div>
        <ActionBox
          action={selectedAction}
          selectedFlight={selectedFlight}
          flights={networkData.flights}
          unmatchedInfo={unmatchedInfo}
        />
      </div>
    </div>
  );
};

export const StreamingWizard = () => {
  const navigate = useNavigate();
  const setError = useSetError();
  const { company = '' } = useParams<{ company: string }>();
  const basePath = `/${company}/streaming/wizard`;
  const { path } = usePageTitleNav({ baseURL: 'streaming/wizard', routes: ROUTES });

  const [dates, setDates] = useState<WizardDateRange>(() => ({
    start: Dfns.format(DATE_FORMAT)(Dfns.subDays(60)(new Date())),
    end: Dfns.format(DATE_FORMAT)(Dfns.addDays(1)(new Date()))
  }));

  const wizardQuery = useStreamingWizardData(company, dates);
  const { data: wizardData, error: wizardError, isLoading: wizardIsLoading, refetch } = wizardQuery;

  useEffect(() => {
    if (wizardError) {
      setError({
        message: wizardError.message,
        reportError: wizardError
      });
    }
  }, [wizardError, setError]);

  const [message, setMessage] = useState('');
  const [wizardHidden, setWizardHidden] = useState(false);

  const [saving, setSaving] = useState(false);

  const resetWizard = useCallback(() => {
    navigate(basePath, { replace: true });
    setSaving(false);
    refetch();
  }, [navigate, basePath, refetch]);

  const contextValue = useMemo(
    () => ({ setMessage, setSaving, resetWizard }),
    [setMessage, setSaving, resetWizard]
  );

  return (
    <Page
      title={<BreadcrumbTitle path={path} title='Streaming Wizard' basePath={basePath} />}
      pageType='Streaming Wizard'
      minHeight='600px'
      minWidth='900px'
      isLoading={wizardIsLoading}
      actions={
        <div>
          <DatePicker range={{ start: dates.start, end: dates.end }} onChange={setDates} />
        </div>
      }
    >
      <WizardContext.Provider value={contextValue}>
        <div className='streaming-wizard'>
          <Routes>
            <Route
              path=':derivedNetwork'
              element={wizardData ? <Details wizardData={wizardData} /> : <FullPageSpinner />}
            />
            <Route
              index
              element={<WizardList wizardData={wizardData} isLoading={wizardIsLoading} />}
            />
          </Routes>
          <button
            type='button'
            className={cn('wizard-himself', { hidden: wizardHidden })}
            onClick={() => setWizardHidden(hidden => !hidden)}
          >
            <img src='https://cdn.blisspointmedia.com/assets/img/wizard.png' alt='wizard' />
          </button>
          {!wizardHidden && (
            <button
              type='button'
              className='speech-bubble'
              onClick={() => setWizardHidden(hidden => !hidden)}
            >
              {!path[0]?.resolvedPath && <div>Welcome to Streaming!</div>}
              {message && (
                <div>
                  {message.split('\n').map((chunk, i) => (
                    <p key={i}>{chunk}</p>
                  ))}
                </div>
              )}
              <div className='hint'>(You can click on me to hide me)</div>
            </button>
          )}
          {saving && (
            <div className='saving'>
              <FullPageSpinner />
            </div>
          )}
        </div>
      </WizardContext.Provider>
    </Page>
  );
};
